import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { getApiBaseUrl, getAuthRequestTimeoutMs } from "../../config/runtime";

interface User {
  id: number;
  name: string;
  email: string;
  image?: string;
  userType: string;
  authType: string;
}

/**
 * Structured login error thrown by AuthContext.login().
 * Callers (Login.tsx) can inspect `code` to show the right UI.
 *
 * Codes:
 *   "unverified"      – account exists but email not verified
 *   "bad_credentials" – wrong email or password
 *   "server_error"    – unexpected server failure
 *   "network_error"   – could not reach the server at all
 */
export class LoginError extends Error {
  constructor(
    public readonly code: "unverified" | "bad_credentials" | "server_error" | "network_error",
    message: string
  ) {
    super(message);
    this.name = "LoginError";
  }
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  googleLogin: () => void;
  logout: () => Promise<void>;
  editIndex: number;
  setEditIndex: React.Dispatch<React.SetStateAction<number>>;
  textareaEdit: boolean;
  setTextareaEdit: React.Dispatch<React.SetStateAction<boolean>>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [editIndex, setEditIndex] = useState<number>(0);
  const [textareaEdit, setTextareaEdit] = useState<boolean>(false);

  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);

  useEffect(() => {
    const restoreSession = async () => {
      const storedUser = localStorage.getItem("user");

      // Optimistically hydrate UI from localStorage so the navbar is not empty
      // on page load, but we always validate against the server below.
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser) as User);
        } catch {
          localStorage.removeItem("user");
        }
      }

      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), getAuthRequestTimeoutMs());

      try {
        const response = await fetch(`${apiBaseUrl}/auth/login/success`, {
          credentials: "include",
          signal: controller.signal,
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.user) {
            // Server confirmed a valid session — keep the user with fresh data.
            setUser(data.user);
            localStorage.setItem("user", JSON.stringify(data.user));
            return;
          }
        }

        // Server returned non-ok OR success:false — session is gone.
        // Always clear localStorage so stale state cannot persist past
        // session expiry (fixes B3: stale localStorage mismatch).
        setUser(null);
        localStorage.removeItem("user");

      } catch (error) {
        if ((error as Error).name === "AbortError") {
          console.warn("Auth session check timed out. Continuing without blocking the UI.");
          // On timeout: keep whatever we had from localStorage, do not clear.
          // This avoids a jarring logout when the server is briefly slow.
        } else {
          console.error("Error fetching user:", error);
          // Network error: same policy — keep localStorage state to avoid
          // logging the user out on a transient connectivity blip.
        }
      } finally {
        window.clearTimeout(timeoutId);
        setLoading(false);
      }
    };

    void restoreSession();
  }, [apiBaseUrl]);

  /**
   * Local login. Throws LoginError with a typed code on failure so callers
   * can show the right message (unverified vs wrong password vs server down).
   */
  const login = async (email: string, password: string): Promise<void> => {
    try {
      const res = await axios.post(
        `${apiBaseUrl}/auth/login-local`,
        { email, password },
        { withCredentials: true }
      );
      setUser(res.data.user);
      localStorage.setItem("user", JSON.stringify(res.data.user));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const data = error.response?.data as { error?: string; message?: string } | undefined;
        const code = data?.error;
        const message = data?.message ?? "Login failed. Please try again.";

        if (code === "unverified") {
          throw new LoginError("unverified", message);
        }
        if (code === "bad_credentials") {
          throw new LoginError("bad_credentials", message);
        }
        if (code === "server_error") {
          throw new LoginError("server_error", message);
        }
        if (error.code === "ERR_NETWORK" || !error.response) {
          throw new LoginError("network_error", "Cannot reach the server. Please check your connection.");
        }
      }
      throw new LoginError("server_error", "Login failed. Please try again.");
    }
  };

  const googleLogin = () => {
    const returnTo = typeof window !== "undefined" ? window.location.origin : "";
    const googleAuthUrl = `${apiBaseUrl}/auth/google?returnTo=${encodeURIComponent(returnTo)}`;
    window.open(googleAuthUrl, "_self");
  };

  const logout = async () => {
    try {
      const response = await axios.post(
        `${apiBaseUrl}/auth/logout`,
        {},
        { withCredentials: true }
      );

      if (response.status === 200) {
        setUser(null);
        localStorage.removeItem("user");
      }
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        googleLogin,
        logout,
        editIndex,
        setEditIndex,
        textareaEdit,
        setTextareaEdit,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useGlobalContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
